// #2121-spec-kits
import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  Label,
  Form,
  Input,
  Button,
  Badge,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { translate } from 'react-translate';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { actionGetAvailableAssets } from 'store/actions/assetsActions';
import AssetsService from 'services/AssetsService';
import {
  actionKitSubmitForm,
  actionKitSubmitEdition,
  actionGetKitTypes,
  actionSetEditKit,
  actionGetKit,
  actionGetKitStateMachine,
} from 'store/actions/kitsActions';
import { actionSetRemoteKitEditorStatus } from 'store/actions/managementUserActions';
import AvailableAssetDraggable, { AssetCard } from './AvailableAssetDraggable';
import ReservedAssetList from './ReservedAssetList';
import { toast } from 'react-toastify';

const STATUS_COLOR = {
  k_st_1:           'secondary',
  k_st_disassembled:         'secondary',
  k_st_2:                'success',
  k_st_reserved:             'info',
  k_st_3: 'info',
  k_st_4:  'primary',
  'k_st_5':      'warning',
  k_st_9:       'warning',
  k_st_6:             'danger',
  k_st_8:               'danger',
  k_st_10:              'warning',
  k_st_11:                  'dark',
  k_st_7:              'secondary',
};

const KitsForm = (props) => {
  const dispatch = useDispatch();
  const { t } = props;
  const { id } = useParams();
  const assetIDPrefix = 'av-';

  // eslint-disable-next-line no-unused-vars
  const [loading, setloading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [loadingLocal, setloadingLocal] = useState(false);
  const [_page, setPage] = useState(0);
  const [_limit, setLimit] = useState(10);
  const [totalPage, setTotalPage] = useState(0);
  const [filter, setFilter] = useState(null);

  const { kitEdit } = useSelector((state) => state.kitsReducer);
  const kitTypes = useSelector((state) => state.kitsReducer.types);
  const { is_creating_kits } = useSelector((state) => state.managementUserReducer);
  const stateMachine = useSelector((state) => state.kitsReducer.stateMachine);

  const { data, page, lastPage, limit } = useSelector(
    (state) => state.assetsReducer.availableAssets
  );

  const [availableAssetList, setAvailableAssetList] = useState(data && data.length ? data : []);
  const [reservedAssets, setreservedAssets] = useState(
    kitEdit.assets && kitEdit.assets.length ? kitEdit.assets : []
  );
  const [localAssets, saveLocalAsset] = useState([]);

  const [typeFilter, setTypeFilter] = useState(
    Object.keys(kitEdit).length
      ? kitEdit.type
      : Object.keys(kitTypes).length
        ? kitTypes[Object.keys(kitTypes)[0]].value
        : null
  );

  // Tracking del asset que se está arrastrando — para el DragOverlay y el
  // estado visual "source" del card original (opacity reducida).
  const [activeAsset, setActiveAsset] = useState(null);

  const handleDragStart = (e) => {
    const id = String(e.active.id).replace(assetIDPrefix, '');
    const asset = availableAssetList.find((a) => a._id === id);
    if (asset) setActiveAsset(asset);
  };

  const handleDragCancel = () => setActiveAsset(null);

  const handleDragEnd = (e) => {
    setActiveAsset(null);
    if (e.over && e.over.id === 'droppable') {
      reserveAsset(String(e.active.id).replace(assetIDPrefix, ''));
    }
  };

  const reserveAsset = (assetId) => {
    let currentPage = _page;
    if (totalPage > 1 && _page + 1 >= totalPage) {
      currentPage = _page - 1;
    }

    const asset = availableAssetList.find((a) => a._id === assetId);
    if (reservedAssets.lenght && reservedAssets.find((a) => a._id === asset._id)) {
      return;
    }

    setreservedAssets([...reservedAssets, asset]);
    setAvailableAssetList(availableAssetList.filter((a) => a._id !== assetId));
  };

  const unreserveAsset = (assetId) => {
    if (reservedAssets.length > 0 && availableAssetList.length < _limit) {
      const asset = reservedAssets.find((a) => a._id === assetId);

      if (asset && asset.status !== 'a_st_available' && !localAssets.find((a) => a._id === asset._id)) {
        saveLocalAsset([...localAssets, asset]);
      }

      if (!asset.study_type || asset.study_type === typeFilter) {
        setAvailableAssetList([...availableAssetList, asset]);
      }
    }

    setreservedAssets(reservedAssets.filter((a) => a._id !== assetId));
  };

  const { register, handleSubmit } = useForm({
    defaultValues: {
      type: kitEdit.type,
      name: kitEdit.name,
    },
  });
  const history = useHistory();

  const onSubmit = (data) => {
    if (!data.type.length) return;

    const kitData = {
      ...data,
      assets: reservedAssets.map((a) => a._id),
    };

    if (Object.keys(kitEdit).length) {
      dispatch(actionKitSubmitEdition({ ...kitData, kitId: kitEdit._id }));
    } else {
      dispatch(actionKitSubmitForm(kitData));
    }
  };

  useEffect(() => {
    if (is_creating_kits) {
      history.goBack();
    } else {
      dispatch(actionSetRemoteKitEditorStatus(true));
    }

    if (!Object.keys(kitEdit).length && id) dispatch(actionGetKit(id));
    if (!Object.keys(kitTypes).length)     dispatch(actionGetKitTypes());
    if (!Object.keys(stateMachine).length) dispatch(actionGetKitStateMachine());

    return () => {
      dispatch(actionSetRemoteKitEditorStatus(false));
      dispatch(actionSetEditKit({}));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Object.keys(kitEdit).length && !kitEdit.is_editable) {
      history.goBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kitEdit]);

  useEffect(() => {
    if (data) {
      const filteredData = data.filter((a) => {
        let pass = true;
        for (let ra of reservedAssets) {
          if (a._id === ra._id) {
            pass = false;
            break;
          }
        }
        return pass;
      });

      setloadingLocal(false);
      setPage(page);
      setLimit(limit);
      setTotalPage(lastPage);

      if (localAssets.length && filteredData.length < _limit) {
        let filteredLocalList = localAssets.filter((a) => {
          return !filteredData.find((fd) => fd._id === a._id) && a.study_type === typeFilter;
        });
        setAvailableAssetList([...filteredData, ...filteredLocalList]);
      } else {
        setAvailableAssetList(filteredData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, data]);

  useEffect(() => {
    if (!typeFilter) {
      setTypeFilter(Object.keys(kitTypes)[0]);
    } else {
      dispatch(actionGetAvailableAssets(_page, _limit, filter, typeFilter));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, kitTypes]);

  useEffect(() => {
    if (Object.keys(kitEdit).length && id) {
      // El backend persiste assets como array de IDs. Hidratamos en paralelo
      // para que el ReservedAssetList muestre tipo/marca/serial.
      const raw = kitEdit.assets || [];
      const needsHydration = raw.length > 0 && raw.some((a) => typeof a === 'string');
      if (needsHydration) {
        Promise.all(
          raw.map((a) =>
            typeof a === 'string'
              ? AssetsService.getAsset(a)
                  .then((r) => (r && r.data ? r.data : null))
                  .catch(() => null)
              : Promise.resolve(a)
          )
        ).then((arr) => setreservedAssets(arr.filter(Boolean)));
      } else {
        setreservedAssets(raw);
      }

      setTypeFilter(kitEdit.type);
      const typeDropdown = document.getElementById('type');
      if (typeDropdown) {
        typeDropdown.value = kitEdit.type;
        typeDropdown.dispatchEvent(new Event('change'));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kitEdit, kitTypes]);

  const changeDataSearch = (page, filterArg) => {
    setloadingLocal(true);
    if (typeFilter) {
      if (filterArg == null) {
        setFilter(null);
        dispatch(actionGetAvailableAssets(page, _limit, null, typeFilter));
      } else {
        setFilter(filterArg);
        dispatch(actionGetAvailableAssets(page, _limit, filterArg, typeFilter));
      }
    }
  };

  const copyIdToClipboard = (e, kitId) => {
    e.preventDefault();
    navigator.clipboard.writeText(kitId);
    toast.success(t('id-copied'));
  };

  const isEditing = !!Object.keys(kitEdit).length;
  const refId = isEditing ? `KITID${kitEdit.friendly_id}KITID` : '';
  const statusColor = isEditing ? (STATUS_COLOR[kitEdit.status] || 'secondary') : 'secondary';

  return (
    <div className="content kit-form-page mt-3">

      {/* Breadcrumb */}
      <div className="kit-form-breadcrumb">
        <button
          type="button"
          className="kit-detail-back"
          onClick={(e) => { e.preventDefault(); history.goBack(); }}
        >
          <i className="now-ui-icons arrows-1_minimal-left" />
          <span>{t('back-to-list') || t('return-btn')}</span>
        </button>
        <span>{t('title-header')}</span>
        <i className="now-ui-icons arrows-1_minimal-right" style={{ fontSize: '0.7rem' }} />
        <span style={{ color: '#2c2c2c', fontWeight: 600, letterSpacing: '0.04em' }}>
          {isEditing ? kitEdit.friendly_id : (t('new-kit') || 'New kit')}
        </span>
      </div>

      {/* Hero compacto */}
      {isEditing && (
        <Card className="kit-form-hero">
          <CardBody>
            <div className="kit-form-hero-flex">
              <div className="kit-form-hero-icon">
                <i className="now-ui-icons shopping_box" />
              </div>
              <div className="kit-form-hero-titles">
                <div className="kit-form-hero-friendly">{kitEdit.friendly_id}</div>
                <div className="kit-form-hero-sub">
                  <div>
                    <span className="kit-form-hero-label">{t('kit-id-label') || 'Kit ID'}</span>
                    <code>{kitEdit._id}</code>
                    <button
                      type="button"
                      className="kit-detail-copy-btn"
                      onClick={(e) => copyIdToClipboard(e, kitEdit._id)}
                      aria-label="Copy"
                    >
                      <i className="now-ui-icons files_single-copy-04" />
                    </button>
                  </div>
                  <div>
                    <span className="kit-form-hero-label">{t('ref-id-label') || 'Reference'}</span>
                    <code>{refId}</code>
                    <button
                      type="button"
                      className="kit-detail-copy-btn"
                      onClick={(e) => copyIdToClipboard(e, refId)}
                      aria-label="Copy"
                    >
                      <i className="now-ui-icons files_single-copy-04" />
                    </button>
                  </div>
                </div>
              </div>
              <Badge color={statusColor} pill style={{
                fontSize: '0.78rem',
                padding: '0.5rem 0.9rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                {t(kitEdit.status)}
              </Badge>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Form */}
      <Form id="kit-form" onSubmit={handleSubmit(onSubmit)}>
        <Card className="kit-form-card">
          <CardBody>
            <div className="kit-form-card-header">
              <i className="now-ui-icons ui-2_settings-90" />
              <span>{t('kit-info') || 'Kit Info'}</span>
            </div>
            <div className="kit-form-fields">
              <div>
                <Label for="type">{t('type')}</Label>
                <Input
                  type="select"
                  name="type"
                  id="type"
                  disabled={reservedAssets.find(a => a.asset_type === "device")}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  innerRef={register}
                >
                  {Object.keys(kitTypes).map((kt, i) => (
                    <option
                      selected={typeFilter === kt}
                      key={`type-${i}`}
                      value={kitTypes[kt].value}
                    >
                      {kitTypes[kt].value}
                    </option>
                  ))}
                </Input>
              </div>
              <div>
                <Label for="status">{t('status')}</Label>
                <Input
                  type="select"
                  name="status"
                  id="status"
                  innerRef={register}
                >
                  {!isEditing
                    ? (
                      <>
                        <option value="k_st_2">{t('k_st_2')}</option>
                        <option value="k_st_1">{t('k_st_1')}</option>
                      </>
                    )
                    : (
                      Object.keys(stateMachine).length
                        ? stateMachine[kitEdit.status].map((st) => (
                            <option key={st} value={st}>{t(st)}</option>
                          ))
                        : null
                    )}
                </Input>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Assets workspace */}
        <Card className="kit-form-card">
          <CardBody>
            <div className="kit-form-card-header">
              <i className="now-ui-icons design_bullet-list-67" />
              <span>{t('assets') || 'Assets'}</span>
            </div>
            <div className="kit-dnd-grid">
              <DndContext
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
              >
                <AvailableAssetDraggable
                  t={t}
                  draggableIDPrefix={assetIDPrefix}
                  changeDataSearch={changeDataSearch}
                  data={availableAssetList}
                  page={_page}
                  totalPage={totalPage}
                  activeId={activeAsset?._id}
                />

                <ReservedAssetList
                  t={t}
                  unreserveAsset={unreserveAsset}
                  reservedAssets={reservedAssets}
                />

                <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(.2,.8,.2,1)' }}>
                  {activeAsset ? <AssetCard asset={activeAsset} isOverlay /> : null}
                </DragOverlay>
              </DndContext>
            </div>

            {/* Sticky action bar */}
            <div className="kit-form-actions">
              <Button
                color="secondary"
                outline
                type="reset"
                onClick={(e) => { e.preventDefault(); history.goBack(); }}
              >
                <i className="now-ui-icons arrows-1_minimal-left" />
                {t('cancel-btn') || t('return-btn')}
              </Button>
              <Button form="kit-form" color="primary" type="submit">
                <i className="now-ui-icons ui-1_check" />
                {t('save-btn')}
              </Button>
            </div>
          </CardBody>
        </Card>
      </Form>
    </div>
  );
};

export default translate('kits')(KitsForm);
